import {
  createContext,
  useState,
  useContext,
  useEffect
} from 'react';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CognitoProvider, poolData } from "./CognitoProvider";
import { VoidFunction } from '../../../types';
import { AppLayout } from '../../../layouts/AppLayout/AppLayout';
import { fetchError } from '../../../redux/actions/Common';
import { useAppDispatch } from "../../../utils/hooks";
import { clearBeaconGroups } from '../../../redux/actions/BusinessGroup';
import { clearBeacons, getUserSubscription } from '../../../redux/actions/Beacon';
import { clearCategories } from '../../../redux/actions/Category';
import { CircularProgress, Box } from '@mui/material';
import { PricingTier } from '../../subscriptions/SubscriptionTable';

interface AuthData {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

interface AuthContextType {
  user: any;
  preferredUserName: string | undefined;
  userRoles: string[] | null;
  pricingList: PricingTier[];
  isRolesRetrieved: boolean;
  isSSOUser: boolean;
  setUser: (_username: string) => void;
  setUserRoles: (_userRoles: string[] | null) => void;
  isBusinessUser: () => boolean;
  signin: (_username: string, _password: string, _callback: any) => void;
  signout: (_callback: VoidFunction) => void;
  signup: (_username: string, _password: string, _callback: any) => void;
  forgotPassword: (_username: string, _callback: any) => void;
  confirmPassword(_username: string, _verificationCode: string, _newPassword: string, _callback: any): void;
  verify: (_username: string, _code: string, _callback: any) => void;
  resendVerification: (_username: string, _callback: any) => void;
  getUserToken: () => Promise<string | null>;
  retrieveUserRoles: (_callback: (success: boolean, roles: string[]) => void) => void;
  changePassword: (_oldPassword: string, _newPassword: string, _callback: (success: boolean, error: string | undefined) => void) => void;
  createSessionWithSSO: (data: AuthData) => void;
}

let AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  let location = useLocation();
  let navigate = useNavigate();
  const dispatch = useAppDispatch();
  let [user, setUser] = useState<any>(null);
  let [preferredUserName, setPreferredUserName] = useState<string>();
  let [userRoles, setUserRoles] = useState<string[] | null>(null);
  let [initializing, setInitializing] = useState<boolean>(true);
  let [isRolesRetrieved, setIsRolesRetrieved] = useState<boolean>(false);
  let [pricingList, setPricingList] = useState<PricingTier[]>([]);
  let [isSSOUser, setIsSSOUser] = useState<boolean>(false);
  const tiers = ["tier_starter", "tier_professional", "tier_enterprise", "tier_pro", "tier_unlimited"];

  useEffect(() => {
    if (initializing && user !== null) {
      return;
    }
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
      // Attempt to get the user from the session. If they're not logged in,
      var cognitoUser = userPool.getCurrentUser();
  
    if (cognitoUser !== null) {
        // NOTE: getSession must be called to authenticate user before calling getUserAttributes
      cognitoUser.getSession((err: Error | null, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
        if (err) {
          alert(err.message || JSON.stringify(err));
          setInitializing(false);
            return <Navigate to="/login" state={{ from: location }} replace />;
        }
  
        retrieveUserRoles((success: boolean, roles: string[]) => {
          if (success) {
            setUserRoles(roles);
            

            // Google for now, add Apple later
            const isSSOUser = roles.some(role => /Google/.test(role));
            setIsSSOUser(isSSOUser);
          }
        });
  
        cognitoUser!.getUserAttributes((err: any, attributes: any) => {
          if (err) {
              // Handle error
            console.error(err);
            setInitializing(false);
              return <Navigate to="/login" state={{ from: location }} replace />;
            } else {
              attributes.forEach((attribute: any ) => {
            switch (attribute.Name) {
              case "email":
                    setUser(attribute.Value);
                break;
              case "preferred_username":
                setPreferredUserName(attribute.Value);
                break;
            }
          });
  
          setInitializing(false);
          }
        });
      });
    } else {
      setInitializing(false);
    }
  }, [user, location, initializing]);

  const retrieveUserRoles = async (callback: (success: boolean, roles: string[]) => void) => {
    CognitoProvider.getSession()
      .then((session: AmazonCognitoIdentity.CognitoUserSession | null) => {
        if (session == null) {
          callback(false, []);
        } else {
          const cognitoRoles = session.getAccessToken().payload["cognito:groups"] ?? [];

            if (!userRoles) {
            dispatch(getUserSubscription((success, userSubscription, pricingTiers) => {
              if (success) {
                const userRoles = [cognitoRoles];
                if (userSubscription) {
                  userRoles.push(userSubscription);
                }
                callback(true, userRoles);
                setIsRolesRetrieved(true);
                setPricingList(pricingTiers ?? []);
              } else {
                callback(false, []);
                setIsRolesRetrieved(true);
                setPricingList(pricingTiers ?? []);
                // dispatch(fetchError("Failed to fetch current subscription"));
              }
            }));
          }
        }
      })
      .catch((error: Error | null) => {
        console.error('Error getting Cognito session:', error);
        callback(false, []);
      });
  };
  

  const isBusinessUser = () => {
      return tiers.some(tier => userRoles?.includes(tier));
  };

  const signin = async (username: string, password: string, callback: (any)) => {
    return await CognitoProvider.signin(username, password, (values: { success: boolean, data: any, errorMessage?: string }) => {

      if (values.data.error) {
        if (values.data.error.code === "UserNotConfirmedException") {
            // Specific error where user is not confirmed
            callback(values.success, true, undefined);
        } else {
            // Other specific errors or fallback to a general error occurred
            callback(values.success, false, values.data.error.message || "An error occurred.");
        }
      } else {
        // No error and sign-in was successful
        callback(values.success, false, undefined);
      }
    });
  };

  // Creates a login session with SSO login method using tokens and sets User
  const createSessionWithSSO = (data: AuthData) => {
    const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({
      AccessToken: data.access_token,
    });

    const IdToken = new AmazonCognitoIdentity.CognitoIdToken({
      IdToken: data.id_token,
    });

    const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
      RefreshToken: data.refresh_token,
    });

    const sessionData = {
      IdToken: IdToken,
      AccessToken: AccessToken,
      RefreshToken: RefreshToken,
    };

    const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

    var poolData = { 
      UserPoolId: process.env.REACT_APP_AMAZON_COGNITO_USER_POOL_ID!, 
      ClientId: process.env.REACT_APP_AMAZON_COGNITO_CLIENT_ID!, 
    };
     
    var userPool = new AmazonCognitoIdentity.CognitoUserPool( 
      poolData 
    ); 

    var userData = { 
      Username: AccessToken.payload.username, 
      Pool: userPool, 
    }; 
 
    var cognitoUser = new AmazonCognitoIdentity.CognitoUser( userData ); 

    // Set the cognito user session with the session created
    cognitoUser.setSignInUserSession(session);

    // Once the session and user has been set navigate to the dashboard
    navigate("/beacons");
  };

  const signout = async (callback: VoidFunction) => {
    dispatch(clearBeaconGroups());
    dispatch(clearBeacons());
    dispatch(clearCategories());
    return await CognitoProvider.signout(() => {
      setUser(null);
      setIsSSOUser(false);
      setUserRoles(null);
      localStorage.removeItem("lastBeaconGroup");
      callback();
    });
  };

  const signup = async (username: string, password: string, callback: (any)) => {
    return await CognitoProvider.signup(username, password, (result: any) => {
      callback(result);
    });
  };

  const forgotPassword = async (username: string, callback: (any)) => {
    return await CognitoProvider.forgotPassword(username, (result: any) => {
      callback(result);
    });
  };

  const confirmPassword = async (username: string, verificationCode: string, newPassword: string, callback: (any)) => {
    return await CognitoProvider.confirmPassword(username, verificationCode, newPassword, (result: any) => {
      callback(result);
    });
  };

  const verify = async (username: string, code: string, callback: any) => {
    return await CognitoProvider.verify(username, code, (result: any) => {
      callback(result);
    });
  };

  const resendVerification = async (username: string, callback: any) => {
    // TODO: implement
  };

  const getUserToken = async (): Promise<string | null> => {

    let sessionPromise = await CognitoProvider.getSession();
    if (sessionPromise == null) {
      fetchError("Could not authenticate request.\nLogging out");
      signout(() => navigate("/"));
      return null;
    } else {
      let token = sessionPromise.getAccessToken().getJwtToken();
      return token;
    }
  };
  const value = { user, preferredUserName, userRoles, isRolesRetrieved, pricingList, isSSOUser, signin, signout, signup, forgotPassword, confirmPassword, verify, resendVerification, setUser, setUserRoles, getUserToken, retrieveUserRoles, isBusinessUser, changePassword, createSessionWithSSO };

  return (initializing ?
    <AuthContext.Provider value={value}><AppLayout /></AuthContext.Provider> :
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>);
}

export function useAuthContext() {
  return useContext(AuthContext);
}

export function RequireAuth({ setPermissionError, children }: { setPermissionError: (displayError: boolean) => void, children: any }) {
  let auth = useAuthContext();
  let location = useLocation();
  let navigate = useNavigate();
  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  var cognitoUser = userPool.getCurrentUser();
  const tiers = ["tier_starter", "tier_professional", "tier_enterprise", "tier_pro", "tier_unlimited"];

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (auth.isRolesRetrieved) {
      setIsLoading(false);
    }

    if (!cognitoUser && !auth.user) {
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
        navigate("/login", { state: { from: location }, replace: true });
    }

  }, [auth.isRolesRetrieved, auth.user, cognitoUser, location, navigate]);

  var hasBusinessPermission = tiers.some(tier => auth.userRoles?.includes(tier)) ?? false;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
      <CircularProgress />
      </Box>
    );
  }
  // May need this in future
  // if (!hasBusinessPermission) {
  //   //business permissions may not have been retrieved before now
  //   console.log("No business permission");
  //   auth.retrieveUserRoles((success: boolean, roles: string[]) => {
  //     if (success) {
  //       hasBusinessPermission = tiers.some(tier => roles.includes(tier)) || 
  //       (auth.userRoles?.some(tier => tiers.includes(tier)) ?? false);
  //       if (!hasBusinessPermission) {
  //         // If we want the user redirected straight to subscription page post login / new registration when they arent subscribed, enable this
  //         // navigate("/subscribe");
  //       }
  //     }
  //   });
  // }

  return children;
}

export const changePassword = async (oldPassword: string, newPassword: string, callBack: (success: boolean, error: string | undefined) => void) => {
  return CognitoProvider.changePassword(oldPassword, newPassword, (success, error) => {
    callBack(success, error);
  });
};